import { Close, WarningAmberRounded } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { COLORS } from "assets";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BoxModel, LabelValueType, MILLISECONDS, ThreatModel } from "types";
import { getSiteLabel } from "utils";

interface Data {
  threats?: ThreatModel[];
  totalRecords?: number;
}
interface DataBox {
  boxes?: BoxModel[];
  totalRecords?: number;
}
interface IData extends LabelValueType<number> {
  unresolved: number;
}

export default function UnresolvedThreats() {
  const { callFetch: callFetchGet } = useFetch<Data>("GET");
  const { callFetch: callFetchBox } = useFetch<DataBox>("GET");
  const [data, setData] = useState<IData[]>([]);

  const t = useTranslation();
  const removeData = useCallback((label: string) => {
    setData((prevState) => prevState.filter((item) => item.label !== label));
  }, []);

  const alerts = useMemo(() => data.filter((item) => item.unresolved > 0), [data]);

  useEffect(() => {
    const getThreats = async () => {
      const searchParams = new URLSearchParams();
      searchParams.append("limit", "0");
      let params = "?" + searchParams.toString();
      const response = await callFetchBox(ENDPOINTS.boxes + params).catch((err) => {
        console.error(err);
        return null;
      });
      const boxes = response?.data?.boxes;
      if (!boxes) return;
      let threats: ThreatModel[] = [];
      const threatsPromises = boxes.map(async (box) => {
        const searchParams = new URLSearchParams();
        searchParams.append("limit", "0");
        searchParams.append("from", new Date(Date.now() - MILLISECONDS.MONTH).toISOString());
        searchParams.append("boxId", box._id);
        let params = "?" + searchParams.toString();
        const response = await callFetchGet(ENDPOINTS.threats + params).catch((err) => {
          console.error(err);
          return null;
        });
        const threatsRes = response?.data?.threats;
        if (threatsRes) {
          threats.push(...threatsRes);
        }
      });
      await Promise.all(threatsPromises);
      let item: { [key in string]: IData } = {};
      threats?.forEach(({ box: { site }, status }) => {
        if (item[site._id]) {
          item[site._id].value += 1;
          item[site._id].unresolved += status !== "Resolved" ? 1 : 0;
        } else {
          item[site._id] = {
            label: getSiteLabel(site),
            value: 1,
            unresolved: status !== "Resolved" ? 1 : 0
          };
        }
      });
      setData(Object.values(item));
    };
    getThreats();
  }, [callFetchGet, callFetchBox]);

  if (!alerts.length) return null;

  return (
    <Stack direction="column" spacing={1} mb={1.5}>
      {alerts.map(({ label, unresolved }, i) => (
        <Box
          key={i}
          width="100%"
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
          sx={{
            bgcolor: "rgba(255, 77, 94, 0.08)",
            border: `1px solid rgba(255, 77, 94, 0.22)`,
            borderRadius: `${COLORS.radius.md}px`,
            px: 1.5,
            py: 1.25,
            minHeight: 48
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0} flex={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: `${COLORS.radius.sm}px`,
                bgcolor: "rgba(255, 77, 94, 0.14)",
                color: COLORS.error
              }}
            >
              <WarningAmberRounded sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.surface.primaryText,
                fontSize: 13,
                lineHeight: 1.4,
                minWidth: 0
              }}
            >
              <Box component="span" fontWeight={700} sx={{ color: COLORS.error }}>
                {t["Caution"]}:
              </Box>
              &nbsp;
              <Box component="span" sx={{ color: COLORS.surface.secondaryText }}>
                {t["Threats remain unresolved for Site"]}&nbsp;&quot;{label}&quot;
              </Box>
            </Typography>
            <Box
              component="span"
              aria-label={`${unresolved} unresolved`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 28,
                height: 28,
                px: 0.75,
                flexShrink: 0,
                bgcolor: COLORS.error,
                color: COLORS.white,
                fontSize: 12,
                fontWeight: 700,
                lineHeight: 1,
                borderRadius: `${COLORS.radius.sm}px`,
                boxSizing: "border-box"
              }}
            >
              {unresolved}
            </Box>
          </Stack>
          <IconButton
            size="small"
            onClick={() => removeData(label)}
            aria-label="Dismiss"
            sx={{
              color: COLORS.surface.secondaryText,
              flexShrink: 0,
              borderRadius: `${COLORS.radius.sm}px`,
              "&:hover": {
                bgcolor: "rgba(255, 77, 94, 0.1)",
                color: COLORS.error
              }
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
}
